import React, { useState } from "react";
import { GetCommanWay, UpdateCommanWay, CreateCommanWay } from "API/commonApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import moment from "moment";
import UseToast from "utils/AfToast";
import Spinner from "utils/Spinner";
import { Button } from "@heroui/react";
import { useNavigate, useParams } from "react-router-dom";
import { PencilIcon, PrinterIcon, TrashIcon } from "@heroicons/react/24/outline";
import Swal from "sweetalert2";

const statusColors: Record<string, string> = {
  working: "bg-green-100 text-green-800",
  not_working: "bg-orange-100 text-orange-800",
  destroyed: "bg-red-100 text-red-800",
  sold: "bg-gray-200 text-gray-700",
  disposed: "bg-gray-200 text-gray-700",
};

const AssetDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: assetResp, isLoading, refetch } = useQuery(
    ["assetDetail", id],
    () => GetCommanWay(`/admin/asset/${id}`, {}, {}),
    { enabled: !!id }
  );

  const asset = assetResp?.status === "SUCCESS" ? assetResp?.data : null;

  const [deletingImg, setDeletingImg] = useState<string | null>(null);

  const { mutate: recalcDepreciation, isLoading: recalculating } = useMutation(
    () => UpdateCommanWay({ url: `/admin/asset/depreciation/${id}`, data: {} }),
    {
      onSuccess: (res: any) => { if (res?.status === "SUCCESS") { UseToast("Recalculated", "success"); refetch(); } else UseToast(res?.message || "Failed", "error"); },
      onError: (err: any) => UseToast(typeof err === "string" ? err : err?.message || "Error", "error"),
    }
  );

  const { mutate: deleteFromAzure } = useMutation(
    (url: string) => CreateCommanWay({ url: "/admin/delete-from-azure", data: { url } }),
    {
      onSuccess: (res: any, deletedUrl: string) => {
        if (res?.status === "SUCCESS") {
          const remaining = (asset.imageLinks || "").split(",").map((l: string) => l.trim()).filter((l: string) => l && l !== deletedUrl).join(",");
          UpdateCommanWay({ url: `/admin/asset/update/${id}`, data: { imageLinks: remaining } }).then((updateRes: any) => {
            if (updateRes?.status === "SUCCESS") {
              UseToast("Image deleted", "success");
              refetch();
            } else {
              UseToast(updateRes?.message || "Failed to update asset", "error");
            }
            setDeletingImg(null);
          });
        } else {
          UseToast(res?.message || "Delete failed", "error");
          setDeletingImg(null);
        }
      },
      onError: (err: any) => { UseToast(typeof err === "string" ? err : err?.message || "Delete failed", "error"); setDeletingImg(null); },
    }
  );

  const handleDeleteImage = (url: string) => {
    Swal.fire({ title: "Delete image?", text: "This will permanently remove the image.", icon: "warning", showCancelButton: true, confirmButtonColor: "#d33", confirmButtonText: "Delete" }).then((r) => {
      if (r.isConfirmed) {
        setDeletingImg(url);
        deleteFromAzure(url);
      }
    });
  };

  const handlePrintTag = () => {
    const pw = window.open("", "_blank", "width=400,height=300");
    if (pw && asset) {
      pw.document.write(`<html><head><title>${asset.assetTag}</title><style>body{font-family:monospace;text-align:center;padding:20px}.tag{border:2px solid #000;padding:15px;display:inline-block}.tag-code{font-size:24px;font-weight:bold;letter-spacing:2px}.tag-name{font-size:14px;margin-top:8px}.tag-branch{font-size:12px;color:#555;margin-top:4px}</style></head><body><div class="tag"><div class="tag-code">${asset.assetTag}</div><div class="tag-name">${asset.name}</div><div class="tag-branch">${asset._subbranch?.branch_name || ""}</div></div><script>window.onload=function(){window.print()}<\/script></body></html>`);
      pw.document.close();
    }
  };

  if (isLoading) return <div className="flex justify-center py-20"><Spinner /></div>;
  if (!asset) return (
    <div className="p-4 text-center">
      <p className="text-slate-500">Asset not found</p>
      <Button variant="flat" className="mt-3" onPress={() => navigate("/asset/list")}>Back</Button>
    </div>
  );

  const imageLinks = asset.imageLinks ? asset.imageLinks.split(",").map((l: string) => l.trim()).filter(Boolean) : [];
  const wExpired = asset.warrantyEndDate && moment(asset.warrantyEndDate).isBefore(moment());

  const Field = ({ label, value, className = "" }: { label: string; value: any; className?: string }) => (
    <div className={`flex items-baseline gap-1 ${className}`}>
      <span className="text-[11px] text-slate-400 whitespace-nowrap">{label}:</span>
      <span className="text-[13px] font-medium text-slate-800 truncate">{value || "-"}</span>
    </div>
  );

  return (
    <div className="bg-gradient-to-br from-purple-400 via-blue-50 to-primary/50 py-2 px-2 font-inter flex-1 min-h-screen">
      {/* Header */}
      <div className="mx-auto mb-2">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-3 py-2 shadow-xl shadow-slate-200/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button onClick={() => navigate("/asset/list")} className="text-slate-500 hover:text-slate-700 text-sm">← Back</button>
              <h1 className="text-base font-bold text-slate-800">{asset.name}</h1>
              <span className="font-mono text-[11px] text-slate-400">{asset.assetTag}</span>
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${statusColors[asset.status] || "bg-gray-100"}`}>
                {asset.status?.replace("_", " ")}
              </span>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="flat" className="h-7 text-xs" onPress={handlePrintTag} startContent={<PrinterIcon className="w-3 h-3" />}>Print Tag</Button>
              <Button size="sm" variant="flat" color="primary" className="h-7 text-xs" onPress={() => navigate(`/asset/edit/${id}`)} startContent={<PencilIcon className="w-3 h-3" />}>Edit</Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content — single card, dense layout */}
      <div className="grid grid-cols-2 md:grid-cols-1 gap-2">
        {/* LEFT */}
        <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm rounded-xl px-3 py-2">
          <div className="grid grid-cols-3 md:grid-cols-2 gap-x-4 gap-y-1.5">
            <Field label="Category" value={asset.category} />
            <Field label="Brand" value={asset.brand} />
            <Field label="Model" value={asset.modelNo} />
            <Field label="Serial" value={asset.serialNo} />
            <Field label="Qty" value={asset.qty || 1} />
            <Field label="Branch" value={asset._subbranch?.branch_name} />
            <Field label="Assigned To" value={asset._assignedTo?.name} />
            <Field label="Status" value={asset.status?.replace("_", " ")} />
            {asset.statusNote && <Field label="Status Note" value={asset.statusNote} className="col-span-3 md:col-span-2" />}
            {asset.description && <Field label="Desc" value={asset.description} className="col-span-3 md:col-span-2" />}
          </div>

          <hr className="my-1.5 border-slate-200" />

          <div className="grid grid-cols-3 md:grid-cols-2 gap-x-4 gap-y-1.5">
            <Field label="Purchase Date" value={asset.purchaseDate ? moment(asset.purchaseDate).format("DD MMM YYYY") : null} />
            <Field label="Price" value={asset.purchasePrice ? `₹${parseFloat(asset.purchasePrice).toLocaleString("en-IN")}` : null} />
            <Field label="Vendor" value={asset._vendor?.name || asset._vendor?.vendor_name} />
            <Field label="Invoice No" value={asset.invoiceNo} />
            <div className="flex items-baseline gap-1">
              <span className="text-[11px] text-slate-400 whitespace-nowrap">Invoice File:</span>
              {asset.invoiceFile ? (
                <a href={asset.invoiceFile} target="_blank" rel="noopener noreferrer" className="text-[12px] text-blue-600 hover:underline font-medium">View / Download</a>
              ) : (
                <span className="text-[13px] text-slate-800">-</span>
              )}
            </div>
          </div>

          {/* Disposal */}
          {(asset.status === "sold" || asset.status === "disposed") && (
            <>
              <hr className="my-1.5 border-slate-200" />
              <div className="grid grid-cols-3 md:grid-cols-2 gap-x-4 gap-y-1.5">
                <Field label="Disposal Date" value={asset.disposalDate ? moment(asset.disposalDate).format("DD MMM YYYY") : null} />
                <Field label="Disposal Price" value={asset.disposalPrice ? `₹${parseFloat(asset.disposalPrice).toLocaleString("en-IN")}` : null} />
                {asset.disposalNote && <Field label="Note" value={asset.disposalNote} className="col-span-3 md:col-span-2" />}
              </div>
            </>
          )}
        </div>

        {/* RIGHT */}
        <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm rounded-xl px-3 py-2">
          <div className="grid grid-cols-3 md:grid-cols-2 gap-x-4 gap-y-1.5">
            <Field label="Warranty Start" value={asset.warrantyStartDate ? moment(asset.warrantyStartDate).format("DD MMM YYYY") : null} />
            <Field label="Warranty End" value={
              asset.warrantyEndDate ? (
                <span className={wExpired ? "text-red-600 font-semibold" : ""}>
                  {moment(asset.warrantyEndDate).format("DD MMM YYYY")}{wExpired && " (Expired)"}
                </span>
              ) : null
            } />
            <Field label="Warranty Info" value={asset.warrantyDetails} />
          </div>

          <hr className="my-1.5 border-slate-200" />

          <div className="grid grid-cols-3 md:grid-cols-2 gap-x-4 gap-y-1.5">
            <Field label="Method" value={asset.depreciationMethod === "WDV" ? "Written Down Value" : "Straight Line"} />
            <Field label="Rate" value={asset.depreciationPercent ? `${asset.depreciationPercent}%` : null} />
            <Field label="Useful Life" value={asset.usefulLifeYears ? `${asset.usefulLifeYears} yrs` : null} />
            <Field label="Residual" value={asset.residualValue ? `₹${parseFloat(asset.residualValue).toLocaleString("en-IN")}` : null} />
            <Field label="Accum. Dep" value={asset.accumulatedDepreciation ? `₹${parseFloat(asset.accumulatedDepreciation).toLocaleString("en-IN")}` : "₹0"} />
            <Field label="Book Value" value={
              <span className="text-blue-700 font-semibold">₹{parseFloat(asset.bookValue || 0).toLocaleString("en-IN")}</span>
            } />
            {asset.lastDepreciationDate && <Field label="Last Calc" value={moment(asset.lastDepreciationDate).format("DD MMM YYYY")} />}
          </div>
          <div className="mt-1.5">
            <Button size="sm" variant="flat" color="primary" className="h-6 text-[11px]" onPress={() => recalcDepreciation()} isLoading={recalculating}>
              Recalculate
            </Button>
          </div>

          {/* Images */}
          {imageLinks.length > 0 && (
            <>
              <hr className="my-1.5 border-slate-200" />
              <div className="flex flex-wrap gap-3">
                {imageLinks.map((url: string, i: number) => (
                  <div key={i} className="relative group">
                    <a href={url} target="_blank" rel="noopener noreferrer">
                      <img src={url} alt={`${i + 1}`} className={`w-20 h-20 object-cover border rounded-lg ${deletingImg === url ? "opacity-40" : ""}`} />
                    </a>
                    <button
                      onClick={() => handleDeleteImage(url)}
                      disabled={!!deletingImg}
                      className="absolute -top-1.5 -right-1.5 bg-red-500 hover:bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity shadow"
                    >
                      <TrashIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Metadata footer */}
      <div className="mt-2 px-1 flex gap-4 text-[10px] text-slate-400">
        {asset._addedBy && <span>Created by: {asset._addedBy.name}</span>}
        {asset.createdAt && <span>Created: {moment(asset.createdAt, "DD-MM-YYYY HH:mm:ss").format("DD MMM YYYY")}</span>}
        {asset._updatedBy && <span>Updated by: {asset._updatedBy.name}</span>}
        {asset.updatedAt && <span>Updated: {moment(asset.updatedAt, "DD-MM-YYYY HH:mm:ss").format("DD MMM YYYY")}</span>}
      </div>
    </div>
  );
};

export default AssetDetail;
